<script setup>
import IconGreekSalad from '@manseImages/yongsin-light/icon-greek-salad.png';
import IconPaintPalette from '@manseImages/yongsin-light/icon-paint-palette.png';
import IconNavigation from '@manseImages/yongsin-light/icon-navigation.png';
import IconNumbers from '@manseImages/yongsin-light/icon-numbers.png';
import IconAlarmClock from '@manseImages/yongsin-light/icon-alarm-clock.png';
import IconHouseGarden from '@manseImages/yongsin-light/icon-house-garden.png';
import IconHandshake from '@manseImages/yongsin-light/icon-handshake.png';
import IconNaturalFood from '@manseImages/yongsin-light/icon-natural-food.png';
import ImgCatGuidePointer from '@manseImages/yongsin-light/img-yongsin-cat-guide-pointer.png';


const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

const props = defineProps({
  isMobile: Boolean,
  contentData: {
    type: Object,
    default: null,
  },
});

// ═══ 왼쪽 페이지: 대운 타임라인 (대운흐름 데이터) ═══
const daeun = computed(() => props.contentData?.daeun ?? null);
const daeunList = computed(() => daeun.value?.timeline ?? []);
const currentDaeun = computed(() => ({
  name: daeun.value?.currentName ?? '',
  age: daeun.value?.currentAge ?? '',
}));
const daeunTitle = computed(() => daeun.value?.title ?? '');
const daeunFlowLines = computed(() => daeun.value?.flowLines ?? []);
const daeunKeywords = computed(() => daeun.value?.keywords ?? []);
const daeunClosingLines = computed(() => daeun.value?.closingBanner ?? []);

const CLOSING_HIGHLIGHT_PHRASE = '지금 흐르는 기운을 이해하고, 용신과 함께 균형을 찾아가는 것';
const highlightClosingLine = line => {
  if (!line) return '';
  if (!line.includes(CLOSING_HIGHLIGHT_PHRASE)) return line;

  return line.split(CLOSING_HIGHLIGHT_PHRASE).join(`<span class="highlight">${CLOSING_HIGHLIGHT_PHRASE}</span>`);
};

// ═══ 오른쪽 페이지 데이터 ═══
const yColor = computed(() => props.contentData?.yongsinColor ?? '#333');
const hColor = computed(() => props.contentData?.heesinColor ?? '#333');
const yOheng = computed(() => props.contentData?.yongsinOheng ?? '');
const hOheng = computed(() => props.contentData?.heesinOheng ?? '');

const colorCard = computed(() => props.contentData?.colorCard ?? {});
const foodCard = computed(() => props.contentData?.foodCard ?? {});
const actionCard = computed(() => props.contentData?.actionCard ?? {});
const directionCard = computed(() => props.contentData?.directionCard ?? {});

const escapeHtml = str => String(str ?? '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

const escapeRegExp = str => String(str ?? '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// "남방(南)" → "남쪽", "중앙(中)" → "중앙"
const extractDirectionKeyword = raw => {
  if (!raw) return '';
  const base = String(raw).replace(/\(.*?\)/g, '').trim();
  if (!base) return '';
  if (base.endsWith('방')) return `${base.slice(0, -1)}쪽`;

  return base;
};

const yongsinDirKeyword = computed(() => extractDirectionKeyword(directionCard.value?.yongsinDirection));
const heesinDirKeyword = computed(() => extractDirectionKeyword(directionCard.value?.heesinDirection));

const highlightDirectionLine = line => {
  let html = escapeHtml(line);
  const targets = [
    { keyword: yongsinDirKeyword.value, color: yColor.value },
    { keyword: heesinDirKeyword.value, color: hColor.value },
  ].filter(t => t.keyword);
  targets.forEach(({ keyword, color }) => {
    const re = new RegExp(escapeRegExp(keyword), 'g');
    html = html.replace(re, `<span class="ysl05-rc__dir-hl" style="color:${color}">${keyword}</span>`);
  });

  return html;
};
const numberCard = computed(() => props.contentData?.numberCard ?? {});
const timeCard = computed(() => props.contentData?.timeCard ?? {});
const spaceCard = computed(() => props.contentData?.spaceCard ?? {});
const relationCard = computed(() => props.contentData?.relationCard ?? {});

// "내 주변" 앞 문구 → 용신색, "을/를 더하면" 앞 문구 → 희신색
const SPACE_MARKERS = [
  { marker: '내 주변', colorKey: 'yongsin' },
  { marker: '을 더하면', colorKey: 'heesin' },
  { marker: '를 더하면', colorKey: 'heesin' },
];

const findSpaceMarker = text => {
  const raw = String(text ?? '');
  if (!raw) return null;

  return SPACE_MARKERS
    .map(m => ({ ...m, idx: raw.indexOf(m.marker) }))
    .filter(m => m.idx >= 0)
    .sort((a, b) => a.idx - b.idx)[0] || null;
};

const wrapSpaceHighlight = (text, colorKey) => {
  const color = colorKey === 'yongsin' ? yColor.value : hColor.value;

  return `<span class="ysl05-rc__space-hl" style="color:${color}">${escapeHtml(text)}</span>`;
};

const spaceHighlightedLines = computed(() => {
  const lines = (spaceCard.value?.lines ?? []).map(l => String(l ?? ''));

  return lines.map((raw, idx) => {
    if (!raw) return '';

    const hit = findSpaceMarker(raw);

    if (hit && hit.idx > 0) {
      const before = raw.slice(0, hit.idx).trimEnd();
      const rest = raw.slice(before.length);

      return `${wrapSpaceHighlight(before, hit.colorKey)}${escapeHtml(rest)}`;
    }

    // 마커가 현재 줄 맨 앞에 있으면 이 줄 자체는 설명문, 앞선 줄(추천 사항)이 이미 색이 입혀짐
    if (hit && hit.idx === 0) return escapeHtml(raw);

    // 마커가 없는 줄: 다음 비어있지 않은 줄이 마커로 시작하면 이 줄이 추천 사항
    for (let j = idx + 1; j < lines.length; j += 1) {
      const next = lines[j];
      if (!next) continue;
      const nextHit = findSpaceMarker(next);
      if (nextHit && nextHit.idx === 0) {
        return wrapSpaceHighlight(raw, nextHit.colorKey);
      }
      break;
    }

    return escapeHtml(raw);
  });
});
</script>

<template>
  <VRow class="full-height">
    <!-- ═══ 왼쪽 페이지: 지금 나에게 흐르는 기운 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="ysl-card full-height right">
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__num">5</span>
          <span class="ysl-title-bar__text">지금 나에게 흐르는 기운</span>
        </div>

        <div class="ysl-content">
          <div class="ysl05-left">
            <!-- ── 대운 타임라인 ── -->
            <div class="ysl05-timeline">
              <div class="ysl05-timeline__line" />
              <div class="ysl05-timeline__dots">
                <div
                  v-for="(d, idx) in daeunList"
                  :key="idx"
                  class="ysl05-timeline__item"
                  :class="{ 'ysl05-timeline__item--current': d.current }"
                >
                  <div class="ysl05-timeline__dot">
                    <span class="ysl05-timeline__name">{{ d.name }}</span>
                  </div>
                  <span class="ysl05-timeline__age">{{ d.age }}</span>
                </div>
              </div>
            </div>

            <!-- ── 대운 소개 카드 ── -->
            <div class="ysl05-intro-card">
              <p class="ysl05-intro-card__sub">{{ currentDaeun?.name }}대운 · {{ currentDaeun?.age }}~</p>
              <p class="ysl05-intro-card__title">{{ daeunTitle }}</p>
            </div>

            <!-- ── 이 시기의 흐름 카드 (content3 뱃지+카드 패턴) ── -->
            <div class="ysl05-flow-section">
              <div class="ysl05-flow-badge">
                <span>이 시기의 흐름</span>
              </div>
              <div class="ysl05-flow-card">
                <p v-for="(line, idx) in daeunFlowLines" :key="'fl' + idx">{{ line }}</p>
              </div>
            </div>

            <!-- ── 키워드 뱃지 ── -->
            <div class="ysl05-keywords">
              <span
                v-for="(kw, idx) in daeunKeywords"
                :key="'kw' + idx"
                class="ysl05-keywords__badge"
                :class="'ysl05-keywords__badge--' + kw.type"
              >{{ kw.label }}</span>
            </div>

            <!-- ── 세로 점 구분선 (파란색 #CAD9FF) ── -->
            <svg class="ysl05-dots" width="8" height="48" viewBox="0 0 8 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="4" cy="4" r="4" fill="#CAD9FF" />
              <circle cx="4" cy="24" r="4" fill="#CAD9FF" />
              <circle cx="4" cy="44" r="4" fill="#CAD9FF" />
            </svg>

            <!-- ── 마무리 메시지 (대운흐름.철학배너) ── -->
            <div v-if="daeunClosingLines.length" class="ysl05-closing">
              <div class="ysl05-closing__box">
                <p v-html="highlightClosingLine(daeunClosingLines[0])" />
                <div class="ysl05-closing__block">
                  <p v-for="(line, i) in daeunClosingLines.slice(1)" :key="'dc' + i" v-html="highlightClosingLine(line)" />
                </div>
              </div>
              <img :src="ImgCatGuidePointer" alt="" class="ysl05-closing__cat" />
            </div>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- ═══ 오른쪽 페이지 (리디자인) ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="ysl-card full-height">
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__num">6</span>
          <span class="ysl-title-bar__text">생활 속 활용 아이디어</span>
        </div>

        <div class="ysl-content ysl-content--right">
          <div class="ysl05-right-body">
            <!-- ── 카드1: 색상 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: colorCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconPaintPalette" alt="" class="ysl05-rc__icon" />
                <div class="ysl05-rc__title-area">
                  <p class="ysl05-rc__title">색상으로 에너지 활용하기</p>
                  <div class="ysl05-rc__swatch-row">
                    <div class="ysl05-rc__swatches">
                      <span
                        v-for="(c, i) in colorCard.yongsinSwatches"
                        :key="'ys' + i"
                        class="ysl05-rc__swatch"
                        :style="{ background: c }"
                      />
                    </div>
                    <span class="ysl05-rc__divider-line" />
                    <div class="ysl05-rc__swatches">
                      <span
                        v-for="(c, i) in colorCard.heesinSwatches"
                        :key="'hs' + i"
                        class="ysl05-rc__swatch"
                        :style="{ background: c }"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div class="ysl05-rc__body">
                <p>
                  안정이 필요할 때는
                  <span class="ysl05-rc__hl" :style="{ color: yColor }">용신색 {{ colorCard.yongsinColors }} 계열</span>을,
                </p>
                <p>
                  에너지가 필요할 때는
                  <span class="ysl05-rc__hl" :style="{ color: hColor }">희신색 {{ colorCard.heesinColors }} 계열</span>을 활용해보세요.
                </p>
              </div>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">{{ colorCard.tags?.yongsin }}</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">{{ colorCard.tags?.heesin }}</span>
              </div>
            </div>

            <!-- ── 카드2: 음식 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: foodCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconGreekSalad" alt="" class="ysl05-rc__icon ysl05-rc__icon--lg" />
                <p class="ysl05-rc__title">{{ foodCard.title }}</p>
              </div>
              <div class="ysl05-rc__body">
                <p v-for="(line, idx) in foodCard.lines" :key="'fd' + idx">{{ line }}</p>
              </div>
              <div class="ysl05-rc__badges">
                <span
                  v-for="(tag, idx) in foodCard.tags"
                  :key="'ft' + idx"
                  class="ysl05-rc__badge"
                  :style="{ color: yColor, background: yColor + '12' }"
                >{{ tag }}</span>
              </div>
            </div>

            <!-- ── 카드3: 행동 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: actionCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconNaturalFood" alt="" class="ysl05-rc__icon" />
                <p class="ysl05-rc__title">용신, 희신 기운을 살리는 행동</p>
              </div>
              <div class="ysl05-rc__body">
                <p v-for="(line, idx) in actionCard.lines" :key="'ac' + idx">{{ line }}</p>
              </div>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">{{ actionCard.tags?.yongsin }}</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">{{ actionCard.tags?.heesin }}</span>
              </div>
            </div>

            <!-- ── 카드4: 방향 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: directionCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconNavigation" alt="" class="ysl05-rc__icon ysl05-rc__icon--lg" />
                <div class="ysl05-rc__title-area">
                  <p class="ysl05-rc__title">공간과 방향 활용하기</p>
                  <div class="ysl05-rc__value-row">
                    <span class="ysl05-rc__value" :style="{ color: yColor }">{{ directionCard.yongsinDirection }}</span>
                    <span class="ysl05-rc__divider-line" />
                    <span class="ysl05-rc__value" :style="{ color: hColor }">{{ directionCard.heesinDirection }}</span>
                  </div>
                </div>
              </div>
              <ul class="ysl05-rc__bullets">
                <li v-for="(item, idx) in directionCard.bullets" :key="'db' + idx" v-html="highlightDirectionLine(item)" />
              </ul>
              <p v-if="directionCard.note" class="ysl05-rc__note">{{ directionCard.note }}</p>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">용신방향</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">희신방향</span>
              </div>
            </div>

            <!-- ── 카드5: 숫자 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: numberCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconNumbers" alt="" class="ysl05-rc__icon" />
                <div class="ysl05-rc__title-area">
                  <p class="ysl05-rc__title">생활 속 숫자 활용하기</p>
                  <div class="ysl05-rc__value-row">
                    <div class="ysl05-rc__num-boxes">
                      <span
                        v-for="(n, i) in numberCard.yongsinNumbers"
                        :key="'yn' + i"
                        class="ysl05-rc__num-box"
                        :style="{ color: yColor, background: yColor + '12' }"
                      >{{ n }}</span>
                    </div>
                    <span class="ysl05-rc__divider-line" />
                    <div class="ysl05-rc__num-boxes">
                      <span
                        v-for="(n, i) in numberCard.heesinNumbers"
                        :key="'hn' + i"
                        class="ysl05-rc__num-box"
                        :style="{ color: hColor, background: hColor + '12' }"
                      >{{ n }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="ysl05-rc__body">
                <p v-for="(line, idx) in numberCard.lines" :key="'nl' + idx">{{ line }}</p>
              </div>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">{{ numberCard.tags?.yongsin }}</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">{{ numberCard.tags?.heesin }}</span>
              </div>
            </div>

            <!-- ── 카드6: 시간 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: timeCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconAlarmClock" alt="" class="ysl05-rc__icon ysl05-rc__icon--lg" />
                <div class="ysl05-rc__title-area">
                  <p class="ysl05-rc__title">나에게 맞는 리듬 찾기</p>
                  <div class="ysl05-rc__value-row">
                    <span class="ysl05-rc__value" :style="{ color: yColor }">{{ timeCard.yongsinTime }}</span>
                    <span class="ysl05-rc__divider-line" />
                    <span class="ysl05-rc__value" :style="{ color: hColor }">{{ timeCard.heesinTime }}</span>
                  </div>
                </div>
              </div>
              <div class="ysl05-rc__body">
                <p v-for="(line, idx) in timeCard.lines" :key="'tl' + idx">{{ line }}</p>
              </div>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">용신시간</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">희신시간</span>
              </div>
            </div>

            <!-- ── 카드7: 공간 꾸미기 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: spaceCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconHouseGarden" alt="" class="ysl05-rc__icon ysl05-rc__icon--lg" />
                <p class="ysl05-rc__title">나만의 공간 꾸미기</p>
              </div>
              <div class="ysl05-rc__body">
                <p v-for="(html, idx) in spaceHighlightedLines" :key="'sl' + idx" v-html="html" />
              </div>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">{{ spaceCard.tags?.yongsin }}</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">{{ spaceCard.tags?.heesin }}</span>
              </div>
            </div>

            <!-- ── 카드8: 관계 ── -->
            <div
              class="ysl05-rc"
              :style="{ borderTopColor: relationCard.borderColor }"
            >
              <div class="ysl05-rc__header">
                <img :src="IconHandshake" alt="" class="ysl05-rc__icon ysl05-rc__icon--lg" />
                <p class="ysl05-rc__title">{{ relationCard.title }}</p>
              </div>
              <div class="ysl05-rc__body">
                <p v-for="(line, idx) in relationCard.lines" :key="'rl' + idx">{{ line }}</p>
              </div>
              <div class="ysl05-rc__badges">
                <span class="ysl05-rc__badge" :style="{ color: yColor, background: yColor + '12' }">{{ relationCard.tags?.yongsin }}</span>
                <span class="ysl05-rc__badge" :style="{ color: hColor, background: hColor + '12' }">{{ relationCard.tags?.heesin }}</span>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
$ysl-primary: #4e80d6;
$ysl-primary-light: rgba(78, 128, 214, 0.05);
$ysl-text: #333;

// ── 공통 (content1~4와 동일 구조) ──
.ysl-card {
  background: #fff;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.right {
    border-right: 1px solid #ebebeb;
  }
}

.ysl-title-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 24px;
  background: $ysl-primary-light;
  flex-shrink: 0;

  &--text-hidden {
    .ysl-title-bar__num,
    .ysl-title-bar__text {
      visibility: hidden;
    }
  }

  &__num {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 11px;
    background: #5d81bf;
    color: #fff;
    font-family: Pretendard;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    flex-shrink: 0;
  }

  &__text {
    color: #5d81bf;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
}

.ysl-content {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
  padding-top: 48px;
  padding-bottom: 100px;
  display: flex;
  flex-direction: column;

  &--right {
    padding-top: 24px;
  }
}

// ═══════════════════════════════
// 왼쪽 페이지: 대운 타임라인
// ═══════════════════════════════
.ysl05-left {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  padding-bottom: 40px; // 고양이 이미지가 카드 밖으로 나가는 공간 확보
}

// ── 대운 타임라인 ──
.ysl05-timeline {
  position: relative;
  width: 340px;
  max-width: 100%;
  height: 80px;
  margin-bottom: 8px; // gap 24 + 8 = 32px (Figma 기준)

  &__line {
    position: absolute;
    top: 33px; // 일반 dot(46px) 중심 = padding-top(10) + 23
    left: -20px;
    right: -20px;
    height: 1px;
    background: #c2c2c2;
  }

  &__dots {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    position: relative;
    height: 100%;
  }

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding-top: 10px; // 일반 dot을 아래로 내려서 라인과 중심 맞춤
  }

  &__dot {
    width: 46px;
    height: 46px;
    border-radius: 50%;
    background: #fff;
    border: 1px solid #ebebeb;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
  }

  &__name {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: #949494;
    white-space: nowrap;
  }

  &__age {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 500;
    line-height: 18px;
    color: #757575;
    white-space: nowrap;
  }

  &__item--current {
    padding-top: 0;

    .ysl05-timeline__dot {
      width: 67px;
      height: 67px;
      border: 2px solid #eb4c10;
      box-shadow: 0 4px 12px rgba(235, 76, 16, 0.3);
    }

    .ysl05-timeline__name {
      font-size: 18px;
      font-weight: 700;
      color: #eb4c10;
    }

    .ysl05-timeline__age {
      color: #eb4c10;
    }
  }
}

// ── 대운 소개 카드 ──
.ysl05-intro-card {
  width: 482px;
  max-width: 100%;
  height: 102px;
  border: 1px solid #eb4c10;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 5px;

  &__sub {
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.5;
    color: #575757;
    margin: 0;
  }

  &__title {
    font-family: Pretendard;
    font-size: 18px;
    font-weight: 700;
    line-height: 24px;
    color: $ysl-text;
    margin: 0;
  }
}

// ── 이 시기의 흐름 카드 (content3 뱃지+카드 패턴) ──
.ysl05-flow-section {
  position: relative;
  width: 100%;
  padding-top: 13px;
}

.ysl05-flow-badge {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 148px;
  height: 26px;
  border-radius: 0 0 10px 10px;
  background: #5d81bf;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  z-index: 2;
  padding: 0 16px;

  span {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: #fff;
    white-space: nowrap;
  }

  // 왼쪽 삼각형 (접힌 효과 - content3 동일, 뱃지보다 진한 색)
  &::before {
    content: '';
    position: absolute;
    left: -10px;
    top: 0;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-right: 10px solid #4a6ca8;
  }
}

.ysl05-flow-card {
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.15);
  padding: 30px 26px;
  text-align: center;

  p {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: $ysl-text;
    margin: 0;
  }
}

// ── 키워드 뱃지 ──
.ysl05-keywords {
  display: flex;
  gap: 8px;
  align-items: center;

  &__badge {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 18px;
    padding: 3px 6px;
    border-radius: 4px;
    white-space: nowrap;

    &--gray {
      background: rgba(148, 148, 148, 0.1);
      color: #949494;
    }

    &--red {
      background: rgba(194, 53, 53, 0.1);
      color: #c23535;
    }
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
  }
}

// ── 세로 점 구분선 ──
.ysl05-dots {
  width: 8px;
  height: 48px;
  object-fit: contain;
}

// ── 마무리 메시지 ──
.ysl05-closing {
  width: 100%;
  position: relative;
  overflow: visible;

  &__box {
    background: #f6f8fd;
    box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.25);
    padding: 40px 24px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;

    p {
      font-family: Pretendard;
      font-size: 15px;
      font-weight: 400;
      line-height: 1.5;
      color: $ysl-text;
      margin: 0;

      strong {
        font-weight: 600;
      }

      :deep(.highlight) {
        font-weight: 700;
        color: #333;
      }
    }
  }

  &__block {
    p {
      margin: 0;
    }
  }

  &__cat {
    position: absolute;
    right: 10px;
    bottom: -70px; // 카드 밖으로 삐져나감
    width: 180px;
    height: 185px;
    object-fit: contain;
    z-index: 1;

    @media (max-width: 480px) {
      width: 140px;
      height: 144px;
      bottom: -90px;
      right: 4px;
    }
  }
}

// ═══════════════════════════════
// 오른쪽 페이지 (리디자인 카드)
// ═══════════════════════════════
.ysl05-right-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

// ── 리디자인 카드 공통 ──
.ysl05-rc {
  width: 100%;
  background: #fff;
  border-top: 2px solid #ccc;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;

  &__header {
    display: flex;
    align-items: center;
    gap: 15px;
  }

  &__icon {
    width: 36px;
    height: 36px;
    object-fit: contain;
    flex-shrink: 0;

    &--lg {
      width: 40px;
      height: 40px;
    }
  }

  &__title-area {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__title {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 600;
    line-height: 24px;
    color: $ysl-text;
    margin: 0;
    word-break: keep-all;
  }

  // ── 색상 스와치 ──
  &__swatch-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__swatches {
    display: flex;
    gap: 5px;
  }

  &__swatch {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    display: block;
  }

  // ── 값 표시 (방향, 시간) ──
  &__value-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__value {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  // ── 숫자 박스 ──
  &__num-boxes {
    display: flex;
    gap: 5px;
  }

  &__num-box {
    width: 20px;
    height: 20px;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 700;
    line-height: 1;
  }

  // ── 구분선 (수직) ──
  &__divider-line {
    width: 1px;
    height: 18px;
    background: #c2c2c2;
    flex-shrink: 0;
  }

  // ── 본문 ──
  &__body {
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    width: 100%;

    p {
      margin: 0;
    }
  }

  &__hl {
    font-weight: 500;
  }

  // ── 불릿 리스트 ──
  &__bullets {
    list-style: disc;
    padding-left: 21px;
    margin: 0;
    font-family: Pretendard;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    color: #575757;
    width: 100%;

    li {
      margin: 0;
    }
  }

  :deep(.ysl05-rc__dir-hl) {
    font-weight: 500;
  }

  :deep(.ysl05-rc__space-hl) {
    font-weight: 500;
  }

  // ── 안내 문구 ──
  &__note {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 400;
    line-height: 1.5;
    color: #757575;
    margin: 0;
  }

  // ── 태그 뱃지 ──
  &__badges {
    display: flex;
    gap: 8px;
    align-items: center;
  }

  &__badge {
    font-family: Pretendard;
    font-size: 13px;
    font-weight: 600;
    line-height: 18px;
    padding: 3px 6px;
    border-radius: 4px;
    white-space: nowrap;
  }
}
</style>
